import React, { useState, useEffect } from "react";
import { Typography, Avatar } from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ThumbnailImage from "../ThumbnailImage/ThumbnailImage";
import "./TestimonialsCard.style.css";
import { AppTheme } from "../../theme/App.theme";

// User Testimonials Component with responsive cards and navigation
const TestimonialCard = ({
  testimonials = [],
  autoPlay = true,
  autoPlayInterval = 5000,
  showNavigation = true,
  className = "",
  style = {},
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoadErrors, setImageLoadErrors] = useState(new Set());

  // Auto-play functionality
  useEffect(() => {
    if (!autoPlay || testimonials.length <= 1 || isHovered) return;

    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
      );
    }, autoPlayInterval);

    return () => clearInterval(timer);
  }, [autoPlay, autoPlayInterval, testimonials.length, isHovered]);

  const handlePrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  const handleNext = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
    );
  };

  const handleImageError = (testimonialId) => {
    setImageLoadErrors(prev => new Set([...prev, testimonialId]));
  };

  if (!testimonials.length) {
    return (
      <div className={`user-testimonials-container ${className}`} style={style}>
        <div className="user-testimonials-empty">
          <Typography variant="body1" color="textSecondary">
            No testimonials available
          </Typography>
        </div>
      </div>
    );
  }

  const currentTestimonial = testimonials[currentIndex];
  const testimonialId = currentTestimonial.id || currentIndex;
  const hasImageError = imageLoadErrors.has(testimonialId);

  return (
    <div 
      className={`user-testimonials-container ${className}`} 
      style={style}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Testimonial Card */}
      <div className="user-testimonials-content">
        {/* Navigation Arrows - using div instead of IconButton */}
        {showNavigation && testimonials.length > 1 && (
          <>
            <div
              className="user-testimonials-nav user-testimonials-nav-left"
              onClick={handlePrevious}
              role="button"
              aria-label="Previous testimonial"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handlePrevious();
                }
              }}
            >
              <ArrowBackIosIcon />
            </div>
            
            <div
              className="user-testimonials-nav user-testimonials-nav-right"
              onClick={handleNext}
              role="button"
              aria-label="Next testimonial"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleNext();
                }
              }}
            >
              <ArrowForwardIosIcon />
            </div>
          </>
        )}

        {/* Main Card */}
        <div className="user-testimonial-card">
          {/* User Info */}
          <div className="user-testimonial-user-info">
            <div className="user-testimonial-avatar">
              {currentTestimonial.profilePicture && !hasImageError ? (
                <ThumbnailImage
                  src={currentTestimonial.profilePicture}
                  size={45}
                  borderRadius="50%"
                  onError={() => handleImageError(testimonialId)}
                />
              ) : (
                <Avatar
                  src={""}
                  alt={(currentTestimonial.userName || "User") + " " + (currentTestimonial.name || "")}
                  sx={{
                    width: { xs: 45, sm: 45, md: 45 },
                    height: { xs: 45, sm: 45, md: 45 },
                    color: AppTheme.PRIMARY_WHITE,
                    bgcolor: AppTheme.PRIMARY_COLOR,
                    fontSize: 22,
                  }}
                >
                  {(currentTestimonial.userName || "U").charAt(0)}
                </Avatar>
              )}
            </div>
            <div className="user-testimonial-user-details">
              <Typography variant="h6" className="user-testimonial-user-name">
                {currentTestimonial.userName || currentTestimonial.name || "Anonymous"}
              </Typography>
            </div>
          </div>

          {/* Testimonial Text */}
          <div className="user-testimonial-text">
            <Typography variant="body1" className="user-testimonial-content">
              {currentTestimonial.testimonial || currentTestimonial.content || currentTestimonial.text}
            </Typography>
          </div>
        </div>
      </div>

      {/* Pagination Dots */}
      {testimonials.length > 1 && (
        <div className="user-testimonials-pagination">
          {testimonials.map((_, index) => (
            <button
              key={index}
              className={`user-testimonials-dot ${
                index === currentIndex ? "active" : ""
              }`}
              onClick={() => setCurrentIndex(index)}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default TestimonialCard;